import { transition } from '@helpers/animate';
import { imageCoverContainer, imageWrapperPreserveRatio } from '@helpers/aspectRatio';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { siteWide } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { link, paragraphRegular } from '@helpers/typography';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';
const Container = styled.footer `
  position: relative;
  overflow: hidden;
  background-color: ${brand.blue.navy};
  border-top: 9px solid ${brand.green.default};

  @media print {
    display: none;
  }
`;
const Inner = styled.div `
  ${siteWide};

  --logoWidth: ${fluid(138, 167)};

  display: grid;
  color: ${brand.global.white};
  padding-top: ${fluid(40, 71)};
  padding-bottom: 0;
  grid-template-columns: 1fr;

  @media ${from(Device.TabletLarge)} {
    grid-template-columns: repeat(12, 1fr);
    column-gap: ${fluid(16, 20)};
  }
`;
const FirstCol = styled.div `
  border-bottom: 1px solid ${brand.global.white};

  @media ${from(Device.TabletLarge)} {
    grid-column: span 2;
    border-bottom: none;
  }
`;
const Logo = styled.a `
  display: block;
  width: var(--logoWidth);
  flex: 0 0 var(--logoWidth);
  margin-bottom: ${fluid(24, 32)};

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: auto;
    outline-offset: -2px;
    outline-width: 2px;
    outline-style: solid;
    outline-color: ${brand.global.white};
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 1;
    grid-row: 1;
  }
`;
const ButtonWrapper = styled.div `
  display: flex;
  gap: ${fluid(8, 32)};
  flex-wrap: wrap;

  @media ${from(Device.TabletLarge)} {
    grid-column: 4 / span 2;
    grid-row: 6;
    margin: auto 0 auto auto;
  }
`;
const LinkListContainer = styled.div `
  margin: ${fluid(12, 24)} 0 ${fluid(0, 40)};

  @media ${from(Device.TabletLarge)} {
    grid-column: 4 / span 6;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: ${fluid(16, 20)};
    margin: ${fluid(24, 24)} 0 ${fluid(36, 52)};
  }
`;
const footerLinkStyles = css `
  ${link};

  text-decoration-color: transparent;
  color: ${brand.global.white};

  &:hover {
    text-decoration-color: currentColor;
  }
`;
const LinkListHeader = styled.button `
  ${ButtonReset};

  color: currentColor;
  margin: 0 0 ${fluid(15, 25)};

  h2 {
    ${fonts.proximaNova.bold};

    font-size: ${fluid(16, 18)};
    line-height: 1.2;
  }

  button& {
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    gap: 24px;
    justify-content: space-between;
    padding: 0 0 15px;
    border-bottom: 1px solid ${brand.global.white};
    padding-right: 6px;

    &:focus-visible {
      outline: auto;
      outline-offset: 5px;
      outline-width: 1px;
    }

    &[data-open='true'] svg {
      transform: rotate(180deg);
    }

    /* Mobile click styles - button should mimic press */
    @media ${until(Device.TabletLarge)} {
      ${transition('transform', 0.15)};

      &:active {
        transform: translateY(1.5px);
      }
    }

    @media ${from(Device.TabletLarge)} {
      display: none;
    }
  }

  a& {
    ${footerLinkStyles};
  }

  a&,
  span& {
    @media ${until(Device.TabletLarge)} {
      display: none;
    }
  }

  svg {
    ${transition('transform', 0.25)};

    display: block;
    width: 12px;
    color: ${brand.green.default};
  }
`;
const LinkList = styled(m.ul) `
  display: flex;
  flex-direction: column;
  gap: ${fluid(16, 24)};
  overflow: hidden;

  li {
    ${fonts.proximaNova.regular};

    font-size: ${fluid(14, 16)};
    line-height: ${fluid(18, 20)};
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  span {
    flex: 0 0 28px;
    background-color: ${brand.green.default};
    border-radius: 100000px;
    height: 28px;
    width: 28px;

    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 17px;
      height: 17px;
    }
  }

  a {
    ${footerLinkStyles};
    ${paragraphRegular};

    display: flex;
    gap: 8px;
    margin: 0;
  }

  @media ${from(Device.TabletLarge)} {
    height: auto !important;
    padding-bottom: 3px;
    margin-bottom: 0 !important;
    visibility: visible !important;

    li[data-mobile-only] {
      display: none;
    }
  }
`;
const AppLinks = styled.div `
  margin-top: ${fluid(0, 24)};
  margin-bottom: 10px;

  @media ${from(Device.TabletLarge)} {
    grid-column: 10 / span 2;
  }
`;
const AppLinkHeader = styled.h2 `
  ${fonts.proximaNova.semiBold};
  font-size: ${fluid(16, 18)};
  line-height: 1.2;
  margin-bottom: ${fluid(16, 26)};
`;
const AppBtnsHolder = styled.div `
  display: flex;
  gap: ${fluid(11, 12)};
`;
const AppLinkBtn = styled.a `
  display: block;
  max-width: ${fluid(103, 114)};

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: auto;
    outline-offset: 3px;
    outline-width: 1px;
    outline-style: solid;
    outline-color: ${brand.global.white};
  }
`;
const AppImage = styled.img `
  object-fit: cover;
  object-position: center;
  aspect-ratio: 114 / 33;
  width: 100%;
`;
const GroupTitle = styled.h2 `
  ${paragraphRegular};

  font-weight: 700;
  margin-bottom: ${fluid(16, 18)};

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const Enquiry = styled.div `
  display: block;
  grid-column: span 1;
  margin-bottom: ${fluid(17, 34)};

  ${LinkList} {
    gap: 8px;
  }

  ${GroupTitle} {
    margin-bottom: ${fluid(6, 12)};

    @media ${until(Device.TabletLarge)} {
      display: block;
      font-size: 16px;
    }
  }
`;
const BaseArea = styled.div `
  border-top: 1px solid ${brand.global.white};
  margin-top: ${fluid(10, 40)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: end;
  gap: 24px;

  @media ${from(Device.TabletLarge)} {
    flex-direction: row;
    grid-row: 2;
    grid-column: 1 / span 12;
  }
`;
const Copyright = styled.div `
  ${fonts.proximaNova.regular};

  display: flex;
  flex-direction: column;
  font-size: 14px;
  line-height: 1.25;
  gap: 16px;

  padding-top: ${fluid(24, 35)};

  p {
    text-align: left;
    margin-bottom: ${fluid(28, 46)};

    span {
      display: inline;

      &:not(:first-child)::before {
        content: '\\2022';
        margin: 0 2ch;
      }
    }

    @media ${until(Device.TabletLarge)} {
      max-width: 48ch;
      margin: 0 auto 0 0;
    }

    @media ${from(Device.TabletLarge)} {
      max-width: 93ch;
    }
  }

  ul {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 16px;

    button {
      ${ButtonReset};
      ${fonts.proximaNova.regular};

      text-align: left;
    }

    button,
    a {
      ${footerLinkStyles};
      font-size: ${fluid(16, 16)};
      line-height: ${fluid(26, 26)};
    }
  }

  @media ${from(Device.TabletLarge)} {
    gap: 35px;
  }
`;
const BackToTopBtn = styled.button `
  ${transition('background-color', 0.25)};
  ${ButtonReset};

  --btnBackgroundColor: ${brand.blue.default};
  --btnSvgColor: ${brand.global.white};

  width: ${fluid(44, 66)};
  height: ${fluid(44, 66)};
  background-color: var(--btnBackgroundColor);
  border-radius: 100000000px;
  border: 2px solid ${brand.blue.default};
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: end;
  margin-top: 24px;

  &:hover {
    --btnBackgroundColor: ${brand.global.white};
    --btnSvgColor: ${brand.blue.default};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: auto;
    outline-offset: -2px;
    outline-width: 2px;
    outline-style: solid;
    outline-color: ${brand.global.white};
    --btnBackgroundColor: ${brand.global.white};
    --btnSvgColor: ${brand.blue.default};
  }

  @media ${until(Device.TabletLarge)} {
    order: -1;
    margin-top: 0;
    margin-bottom: calc(${fluid(44, 66)} * -1);
  }

  svg {
    ${transition('color', 0.25)};
    width: 26px;
    height: 26px;
    color: var(--btnSvgColor);
    transform: rotate(-90deg);
  }
`;
const RegulatoryArea = styled.div `
  display: flex;
  gap: ${fluid(8, 16)};
`;
const RegulatoryLink = styled.a `
  ${imageWrapperPreserveRatio(79, 81)};

  display: block;
  width: ${fluid(79, 144)};

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: auto;
    outline-offset: 2px;
    outline-width: 2px;
    outline-style: solid;
    outline-color: ${brand.global.white};
    --btnBackgroundColor: ${brand.global.white};
    --btnSvgColor: ${brand.blue.default};
  }

  img {
    ${imageCoverContainer()};
  }
`;
const FooterStyles = {
    Container,
    Inner,
    FirstCol,
    Logo,
    ButtonWrapper,
    LinkListContainer,
    LinkListHeader,
    LinkList,
    AppBtnsHolder,
    AppImage,
    AppLinkBtn,
    AppLinks,
    AppLinkHeader,
    GroupTitle,
    Enquiry,
    BaseArea,
    Copyright,
    BackToTopBtn,
    RegulatoryArea,
    RegulatoryLink,
};
export default FooterStyles;
